import type { TaskBaselineEntry, TaskSnapshot } from '../stores/types';
import { parseDateSafe } from '../utils/utils';

/** Option for the Compare baseline selector. */
export interface BaselineCompareOption {
  index: number;
  label: string;
  /** Soft-deleted baseline slot — shown greyed out and not selectable. */
  disabled?: boolean;
}

/**
 * DHTMLX baseline row.
 * Prefer Date objects — string dates on task.baselines skip parseDate and cause "Invalid day index".
 */
export interface DhtmlxBaselineRow {
  id: string;
  task_id: string;
  start_date: Date;
  end_date: Date;
  duration: number;
}

export interface BaselineVarianceDays {
  /** Current start − baseline start (calendar days). Null if either date missing. */
  startDays: number | null;
  /** Current finish − baseline finish (calendar days). Null if either date missing. */
  finishDays: number | null;
  /** Current duration − baseline duration. Null if either duration missing. */
  durationDays: number | null;
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function isValidDate(value: Date | null | undefined): value is Date {
  return !!value && !Number.isNaN(value.getTime());
}

function addCalendarDays(date: Date, days: number): Date {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + days);
  return next;
}

/** Whole calendar-day difference (end − start). */
export function calendarDayDiff(a: Date | null | undefined, b: Date | null | undefined): number | null {
  if (!isValidDate(a) || !isValidDate(b)) return null;
  const ms = startOfLocalDay(a).getTime() - startOfLocalDay(b).getTime();
  return Math.round(ms / (24 * 60 * 60 * 1000));
}

export function getTaskBaseline(
  task: TaskSnapshot | null | undefined,
  index: number,
): TaskBaselineEntry | null {
  if (!task?.baselineInfo?.length) return null;
  return task.baselineInfo.find((b) => b.index === index) ?? null;
}

/** Union of baseline indexes across tasks, sorted descending (latest first). */
export function listAvailableBaselineOptions(
  tasks: TaskSnapshot[],
  disabledIndices?: Iterable<number> | null,
): BaselineCompareOption[] {
  const disabled = new Set<number>();
  if (disabledIndices) {
    for (const index of disabledIndices) {
      if (Number.isFinite(index)) disabled.add(Number(index));
    }
  }
  const byIndex = new Map<number, string>();
  for (const task of tasks) {
    for (const entry of task.baselineInfo ?? []) {
      if (!Number.isFinite(entry.index)) continue;
      const name = entry.name?.trim();
      const existing = byIndex.get(entry.index);
      if (!existing) {
        byIndex.set(entry.index, name || `Baseline ${entry.index}`);
      } else if (name && /^Baseline \d+$/i.test(existing)) {
        byIndex.set(entry.index, name);
      }
    }
  }
  return [...byIndex.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([index, label]) => ({
      index,
      label: disabled.has(index) ? `${label} (deleted)` : label,
      disabled: disabled.has(index),
    }));
}

/**
 * Resolve a drawable baseline range from API fields.
 * Does not invent dates from the live schedule.
 * Returns null when we cannot form a valid start/end pair.
 */
export function resolveBaselineDateRange(entry: TaskBaselineEntry): {
  start: Date;
  end: Date;
  duration: number;
} | null {
  const start = entry.start ? parseDateSafe(entry.start) : undefined;
  const end = entry.finish ? parseDateSafe(entry.finish) : undefined;
  const hasStart = isValidDate(start);
  const hasEnd = isValidDate(end);
  const duration =
    entry.duration != null && Number.isFinite(entry.duration) && entry.duration >= 0
      ? entry.duration
      : null;

  let startDate: Date | null = hasStart ? start : null;
  let endDate: Date | null = hasEnd ? end : null;

  if (startDate && endDate) {
    // both present
  } else if (startDate && !endDate && duration != null) {
    endDate = addCalendarDays(startDate, Math.max(1, duration));
  } else if (endDate && !startDate && duration != null) {
    startDate = addCalendarDays(endDate, -Math.max(1, duration));
  } else if (startDate && !endDate) {
    // Single start date → 1-day bar (honest: no invented schedule dates)
    endDate = addCalendarDays(startDate, 1);
  } else if (endDate && !startDate) {
    startDate = addCalendarDays(endDate, -1);
  } else {
    return null;
  }

  if (!isValidDate(startDate) || !isValidDate(endDate)) return null;
  if (endDate.getTime() < startDate.getTime()) return null;

  // Zero-width ranges are invisible — ensure at least 1 calendar day.
  if (endDate.getTime() === startDate.getTime()) {
    endDate = addCalendarDays(startDate, 1);
  }

  const resolvedDuration =
    duration != null && duration > 0
      ? duration
      : Math.max(1, calendarDayDiff(endDate, startDate) ?? 1);

  return { start: startDate, end: endDate, duration: resolvedDuration };
}

/**
 * Map Current schedule vs Baseline N into DHTMLX baselines[].
 * Only includes rows for `visibleTaskIds` (tasks actually on the chart).
 */
export function buildDhtmlxBaselinesForCompare(
  tasks: TaskSnapshot[],
  baselineIndex: number,
  visibleTaskIds?: Set<string>,
): DhtmlxBaselineRow[] {
  const rows: DhtmlxBaselineRow[] = [];
  for (const task of tasks) {
    const taskId = String(task.id);
    if (visibleTaskIds && !visibleTaskIds.has(taskId)) continue;

    const entry = getTaskBaseline(task, baselineIndex);
    if (!entry) continue;

    const range = resolveBaselineDateRange(entry);
    if (!range) continue;

    rows.push({
      // Stable, unique per task+index — avoid colliding with other datastore ids.
      id: `${taskId}__baseline_${baselineIndex}`,
      task_id: taskId,
      start_date: range.start,
      end_date: range.end,
      duration: range.duration,
    });
  }
  return rows;
}

/** Variance of current schedule vs a baseline entry (+ = later / longer than baseline). */
export function computeCurrentVsBaselineVariance(
  task: TaskSnapshot | null | undefined,
  baselineIndex: number,
): BaselineVarianceDays {
  const entry = getTaskBaseline(task, baselineIndex);
  if (!entry) {
    return { startDays: null, finishDays: null, durationDays: null };
  }
  const currentStart = task?.start ? parseDateSafe(task.start) : undefined;
  const baselineStart = entry.start ? parseDateSafe(entry.start) : undefined;
  const startDays = calendarDayDiff(currentStart, baselineStart);

  const currentFinish = task?.finish ? parseDateSafe(task.finish) : undefined;
  const baselineFinish = entry.finish ? parseDateSafe(entry.finish) : undefined;
  const finishDays = calendarDayDiff(currentFinish, baselineFinish);

  const currentDur = task?.duration;
  const baselineDur = entry.duration;
  const durationDays =
    currentDur != null &&
    baselineDur != null &&
    Number.isFinite(currentDur) &&
    Number.isFinite(baselineDur)
      ? currentDur - baselineDur
      : null;

  return { startDays, finishDays, durationDays };
}

export function formatVarianceDays(days: number | null | undefined): string {
  if (days == null || !Number.isFinite(days)) return '—';
  if (days === 0) return '0d';
  return days > 0 ? `+${days}d` : `${days}d`;
}

export interface BaselineVarianceBiggestSlip {
  taskId: string;
  taskName: string;
  finishDays: number;
}

/** Aggregated current-vs-baseline finish health for the Gantt variance strip. */
export interface BaselineVarianceSummary {
  /** Leaf tasks with a comparable finish variance for this baseline. */
  comparedCount: number;
  onTrackCount: number;
  laterCount: number;
  earlierCount: number;
  /** Leaf tasks that have the baseline slot but no comparable finish dates. */
  missingFinishCount: number;
  /**
   * Latest leaf current finish − latest leaf baseline finish (calendar days).
   * Null when either side cannot be resolved.
   */
  projectFinishDays: number | null;
  biggestSlip: BaselineVarianceBiggestSlip | null;
}

function getLeafTasksForBaselineSummary(tasks: TaskSnapshot[]): TaskSnapshot[] {
  const parentIds = new Set<string>();
  for (const task of tasks) {
    const parentId = task.parentTaskId != null ? String(task.parentTaskId).trim() : '';
    if (parentId) parentIds.add(parentId);
  }
  return tasks.filter((task) => !parentIds.has(String(task.id)));
}

function taskDisplayName(task: TaskSnapshot): string {
  const name = task.taskName?.trim();
  if (name) return name;
  return `Task ${task.id}`;
}

/**
 * Summarize leaf-task finish variance vs an approved baseline slot.
 * On track = exact 0 finish-day delta; later &gt; 0; earlier &lt; 0.
 */
export function summarizeCurrentVsBaseline(
  tasks: TaskSnapshot[],
  baselineIndex: number,
): BaselineVarianceSummary {
  const leaves = getLeafTasksForBaselineSummary(tasks);
  let onTrackCount = 0;
  let laterCount = 0;
  let earlierCount = 0;
  let missingFinishCount = 0;
  let comparedCount = 0;
  let biggestSlip: BaselineVarianceBiggestSlip | null = null;

  let latestCurrentFinish: Date | null = null;
  let latestBaselineFinish: Date | null = null;

  for (const task of leaves) {
    const entry = getTaskBaseline(task, baselineIndex);
    if (!entry) continue;

    const { finishDays } = computeCurrentVsBaselineVariance(task, baselineIndex);
    if (finishDays == null) {
      missingFinishCount += 1;
    } else {
      comparedCount += 1;
      if (finishDays === 0) onTrackCount += 1;
      else if (finishDays > 0) {
        laterCount += 1;
        if (!biggestSlip || finishDays > biggestSlip.finishDays) {
          biggestSlip = {
            taskId: String(task.id),
            taskName: taskDisplayName(task),
            finishDays,
          };
        }
      } else {
        earlierCount += 1;
      }
    }

    const currentFinish = task.finish ? parseDateSafe(task.finish) : undefined;
    const baselineFinish = entry.finish ? parseDateSafe(entry.finish) : undefined;
    if (isValidDate(currentFinish)) {
      if (!latestCurrentFinish || currentFinish.getTime() > latestCurrentFinish.getTime()) {
        latestCurrentFinish = currentFinish;
      }
    }
    if (isValidDate(baselineFinish)) {
      if (!latestBaselineFinish || baselineFinish.getTime() > latestBaselineFinish.getTime()) {
        latestBaselineFinish = baselineFinish;
      }
    }
  }

  return {
    comparedCount,
    onTrackCount,
    laterCount,
    earlierCount,
    missingFinishCount,
    projectFinishDays: calendarDayDiff(latestCurrentFinish, latestBaselineFinish),
    biggestSlip,
  };
}

